'use client';

import type { ReactNode } from 'react';
import { useRenderFormat } from '@/app/components/shared/NotificationCenter/hook/use-render-format';
import type { ProgramActivity, ProgramActivityText } from './types';
import { getProgramActivityStatus, getProgramActivityTimeRange } from './utils';

export function ProgramActivityInfo({
  activity,
  children,
  text,
  title,
  subtitle,
}: {
  activity: ProgramActivity;
  children?: ReactNode;
  text: ProgramActivityText;
  title: string;
  subtitle?: string;
}) {
  const status = getProgramActivityStatus(activity);
  const statusText = { ended: text.ended, in_progress: text.inProgress, not_started: text.notStarted }[status];
  const statusTextClassName = {
    ended: 'text-system-white48',
    in_progress: 'text-[#0086FF]',
    not_started: 'text-system-white',
  }[status];

  return (
    <section className="overflow-hidden rounded-[22px] bg-[#1d1e20] p-5 shadow-[0_28px_80px_rgba(0,0,0,0.3)] sm:p-4">
      <div className="relative aspect-[2.58/1] overflow-hidden rounded-[13px] bg-[#171b25] sm:aspect-[1.64/1]">
        {activity.cover_uri ? (
          <img src={activity.cover_uri} alt={title || text.coverAlt} className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center bg-[linear-gradient(135deg,#17191f,#08090c_58%,#1f2229)] text-white/44">
            {text.coverFallback}
          </div>
        )}
      </div>
      <div className="pt-5">
        <div className="flex items-start justify-between gap-4 sm:gap-3">
          <h2 className="min-w-0 break-words font-semibold text-[28px] text-white leading-tight tracking-normal sm:text-[22px]">
            {title}
          </h2>
          <span
            className={`shrink-0 rounded-full bg-white/[0.08] px-3 py-1 font-semibold text-sm leading-5 ${statusTextClassName}`}
          >
            {statusText}
          </span>
        </div>
        {subtitle && <p className="mt-3 text-[15px] text-white/58 leading-6">{subtitle}</p>}
        <div className="mt-5 flex items-start gap-3 rounded-[14px] bg-[#26272a] px-4 py-4 text-white/88 sm:px-3">
          <span className="mt-1 size-4 shrink-0 rounded-full border-2 border-[#1396ff]" />
          <span>
            {text.eventTime} : {getProgramActivityTimeRange(activity) || text.timePending}
          </span>
        </div>
        {children}
      </div>
    </section>
  );
}

export function ProgramActivityContent({ content, className = '' }: { content: string; className?: string }) {
  const { renderHtmlContent } = useRenderFormat();

  return (
    <section className={`rounded-[14px] bg-[#26272a] px-4 py-4 sm:px-3 ${className}`}>
      <div className="prose prose-img:my-4 prose-video:my-4 whitespace-pre-wrap break-words prose-img:rounded-[13px] prose-video:rounded-[13px] font-normal prose-strong:font-medium prose-a:text-[#1396ff] prose-p:text-white/82 prose-strong:text-white text-[15px] text-white/68 leading-7 prose-a:no-underline hover:prose-a:underline [&_video]:bg-black/20">
        {renderHtmlContent(content)}
      </div>
    </section>
  );
}
